<template>
    <header class="scc-app-header" :class="{ 'scc-app-header--inverted': scrollPosition > 50 }">
        <nav class="scc-main-nav" :class="showMenu ? 'scc-main-nav--hide' : ''">
            <div class="scc-main-nav__section">
                <div class="scc-main-nav__logo">
                    <SccLink :to="{ name: 'Home' }">
                        <img v-if="altLogoUrl && scrollPosition > 50" :src="altLogoUrl" :alt="logoText" />
                        <img v-else-if="logoUrl" :src="logoUrl" :alt="logoText" />
                        <span v-else class="bold">{{ logoText }}</span>
                    </SccLink>
                </div>
                <div class="scc-main-nav__spacer"></div>
                <template v-for="item in menu">
                    <div v-if="!item.cta" :key="item.path" class="scc-main-nav__item">
                        <div v-if="item.path === currentPath" class="scc-main-nav__item-icon"></div>
                        <SccLink class="kwf-menu kwf-bold" :to="item.path">
                            {{ item.title }}
                        </SccLink>
                    </div>
                </template>
                <div class="scc-main-nav-cta">
                    <div v-for="item in ctas" :key="item.path" class="scc-main-nav__item scc-main-nav__item--cta">
                        <SccLink class="cta-button p" tabindex="0" :to="item.path">
                            {{ item.title }}
                        </SccLink>
                    </div>
                </div>
                <button class="scc-main-nav-toggle" aria-labelledby="svgtitle" @click="toggleMenu">
                    <svg xmlns="http://www.w3.org/2000/svg" class="closed" width="18" height="18" viewBox="0 0 24 20">
                        <title id="svgtitle">Menu</title>
                        <rect id="bar_1" width="24" height="3" :transform="navToggleTransform.bar1" />
                        <rect id="bar_3" width="24" height="3" :transform="navToggleTransform.bar2" />
                        <rect id="bar_2" width="24" height="3" :transform="navToggleTransform.bar3" />
                    </svg>
                </button>
            </div>
        </nav>
        <nav class="scc-nav" :class="!showMenu ? 'scc-nav--hide' : ''">
            <div class="scc-main-nav__section">
                <div class="scc-nav__logo">
                    <SccLink :to="{ name: 'Home' }" @click="toggleMenu">
                        <img v-if="logoUrl" :src="logoUrl" :alt="logoText" />
                        <span v-else class="bold">{{ logoText }}</span>
                    </SccLink>
                </div>
                <button class="scc-nav-toggle" aria-labelledby="svgtitle" @click="toggleMenu">
                    <svg xmlns="http://www.w3.org/2000/svg" class="open" width="18" height="18" viewBox="0 0 24 20">
                        <title id="svgtitle">Menu</title>
                        <rect id="bar_1" width="24" height="3" :transform="navToggleTransform.bar1" />
                        <rect id="bar_3" width="24" height="3" :transform="navToggleTransform.bar2" />
                        <rect id="bar_2" width="24" height="3" :transform="navToggleTransform.bar3" />
                    </svg>
                </button>
            </div>
            <div class="scc-nav-links">
                <template v-for="item in menu">
                    <div v-if="!item.cta" :key="item.path" class="scc-nav__item">
                        <div class="scc-nav__item-icon"></div>
                        <SccLink class="h5 bold" :to="item.path" @click="toggleMenu">
                            {{ item.title }}
                        </SccLink>
                    </div>
                </template>
                <template v-for="item in ctas">
                    <span :key="item.path">
                        <SccLink class="scc-nav__button" button color="primary" :to="item.path" @click="toggleMenu">
                            {{ item.title }}
                        </SccLink>
                    </span>
                </template>
            </div>
            <div class="scc-nav__accent-shape"></div>
        </nav>
    </header>
</template>
<script>
import './app-header.scss';
import SccLink from '../global/SccLink';
import { ticketSaleOpen } from '@/utils/data-conversions';

export default {
    name: 'AppHeader',
    components: { SccLink },
    __editor: {
        fields: {
            logoUrl: {
                widget: 'media',
            },
            altLogoUrl: {
                widget: 'media',
            },
            menuItems: {
                widget: 'array',
                values: {
                    flat: false,
                    fields: {
                        title: {
                            widget: 'text',
                        },
                        destination: {
                            widget: 'page',
                        },
                        showCondition: {
                            widget: 'select',
                            values: [
                                { value: 'always', text: 'Always' },
                                { value: 'hasPublishedDrawing', text: 'Has published drawing' },
                                { value: 'ticketSaleOpen', text: 'Ticket sale open' },
                            ],
                        },
                    },
                },
            },
        },
        requiredData: ['game'],
    },
    props: {
        game: {
            type: Object,
            required: true,
        },
        logoUrl: String,
        altLogoUrl: String,
        logoText: {
            type: String,
            default: 'Home',
        },
        ctaText: {
            type: String,
            default: 'Doe mee',
        },
        menuItems: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    data() {
        return {
            showMenu: false,
            currentPath: window.location.pathname.substring(1),
            scrollPosition: null,
            scrollPositionSnapshot: null,
            window: null,
        };
    },
    computed: {
        ticketSaleOpen() {
            return ticketSaleOpen(this.game);
        },
        navToggleTransform() {
            if (this.showMenu) {
                return {
                    bar1: 'translate(4.929 0.201) rotate(45)',
                    bar2: 'translate(2.101 16.971) rotate(-45)',
                    bar3: null,
                };
            } else {
                return {
                    bar1: null,
                    bar2: 'translate(0 8)',
                    bar3: 'translate(0 16)',
                };
            }
        },
        menu() {
            const menu = [];

            this.menuItems.forEach(({ title, destination, showCondition }) => {
                if (
                    (showCondition === 'hasPublishedDrawing' && !this.game.hasPublishedDrawing) ||
                    (showCondition === 'ticketSaleOpen' && !this.ticketSaleOpen)
                ) {
                    return;
                }

                menu.push({ path: destination.path, title });
            });

            if (this.ticketSaleOpen) {
                menu.push({
                    path: 'aanmelden',
                    title: this.ctaText,
                    cta: true,
                });
            }

            return menu;
        },
        ctas() {
            return this.menu.filter(item => {
                return item.cta;
            });
        },
    },
    watch: {
        showMenu(showMenu) {
            if (showMenu) {
                this.saveScrollPosition();
                document.body.classList.add('modal-open');
                return;
            }

            document.body.classList.remove('modal-open');
            this.restoreScrollPosition();
        },
        $route() {
            const newPath = window.location.pathname.substring(1);

            if (this.currentPath !== newPath) {
                this.initializeScrollPosition();
            }

            this.currentPath = newPath;
        },
    },
    mounted() {
        this.addWindowScrollEventListener(window);
    },
    previewLoaded() {
        this.addWindowScrollEventListener(this?.$editor?.preview?.window);
    },
    beforeDestroy() {
        this.removeWindowScrollEventListener(this.window);
    },
    methods: {
        toggleMenu() {
            this.showMenu = !this.showMenu;
        },
        updateScrollPosition() {
            this.scrollPosition = this.window.scrollY;
        },
        initializeScrollPosition() {
            this.window.scrollTo(0, 0);
            this.scrollPositionSnapshot = null;
        },
        saveScrollPosition() {
            this.scrollPositionSnapshot = this.window.scrollY;
        },
        restoreScrollPosition() {
            if (!this.scrollPositionSnapshot) {
                return;
            }

            this.window.scrollTo(0, this.scrollPositionSnapshot);
            this.scrollPositionSnapshot = null;
        },
        addWindowScrollEventListener(window) {
            this.removeWindowScrollEventListener(this.window);
            window?.addEventListener('scroll', this.updateScrollPosition);
            this.window = window;
        },
        removeWindowScrollEventListener(window) {
            window?.removeEventListener('scroll', this.updateScrollPosition);
        },
    },
};
</script>
